import { handleFetchMore } from 'components/InfiniteList'
import {
  useGetVersionHistoryQuery,
  GetVersionHistoryQuery,
  ShareInfoFragment,
} from 'gqlTypes'
import { ShareWithoutVersion } from 'modules/versioning/ShareVersionContext/ShareVersionContext'

const entriesPath = ['share', 'versionHistory', 'entries']
const afterPath = ['share', 'versionHistory', 'meta', 'after']

export type VersionItem = NonNullable<
  GetVersionHistoryQuery['share']
>['versionHistory']['entries'][0]

export interface UseVersionHistoryQueryResult {
  loadLatestHandler: () => Promise<unknown>
  totalCount: number
  loadMoreHandler: () => Promise<unknown>
  entries: VersionItem[]
}

const filterEntriesWithoutPermission = (
  versionTypeAvailableToUser: ShareWithoutVersion['versionTypeAvailableToUser'],
  entries: VersionItem[]
) =>
  entries.filter((entry, key) => {
    switch (versionTypeAvailableToUser) {
      case 'ANY':
        return entry
      case 'ANY_STARRED':
        if ('kind' in entry && entry.kind === 'PUBLISHED') {
          return entry
        }
        break
      case 'LATEST':
        // in here, the most recent entry is the first one
        if (key === 0) {
          return entry
        }
        break
    }
    return undefined
  })

export const useVersionHistoryQuery = (
  shareID: string,
  versionTypeAvailableToUser: ShareInfoFragment['versionTypeAvailableToUser']
): UseVersionHistoryQueryResult | undefined => {
  const { loading, error, data, fetchMore } = useGetVersionHistoryQuery({
    variables: {
      shareIdentifier: shareID,
      after: null,
    },
    fetchPolicy: 'cache-and-network',
  })
  const entries = data?.share?.versionHistory?.entries || []

  /**
   * as of apollo-client@2.6.2:
   * somehow using fetchMore with the same variables as original query
   * triggers loading state to change (where as if we are using different
   * variables - that doesn't change)
   * as a workaround to overcome this issue we are checking
   * if there are any data from previous render
   * (which is a good thing most of the times either way)
   */
  if (loading && entries.length === 0) return undefined

  if (error) return undefined

  const totalCount = data?.share?.availableVersions?.meta.totalCount

  if (totalCount === undefined) {
    // TODO: report an error which should never happen
    // https://github.com/sketch-hq/Cloud/issues/
    return undefined
  }

  const entriesWithPermissions = filterEntriesWithoutPermission(
    versionTypeAvailableToUser,
    entries
  )

  const after = data?.share?.versionHistory?.meta.after || null
  const loadMoreHandler = handleFetchMore(fetchMore, entriesPath, {
    dataIdFromObject: (obj: any) => obj.identifier,
    after,
  })

  const loadLatestHandler = handleFetchMore(fetchMore, entriesPath, {
    dataIdFromObject: (obj: any) => obj.identifier,
    fetchLatest: true,
    preserveAfter: true,
    afterPath,
  })

  return {
    loadLatestHandler,
    totalCount,
    loadMoreHandler,
    entries: entriesWithPermissions,
  }
}
